import React, { createContext, ReactNode, useContext } from 'react';

type AdornmentSlot =
    | 'select'
    | 'score'
    | 'explanation'
    | 'explanationDetailedView'
    | 'scoreDetailedView'
    | 'detailProfileActions';
interface ProfileAdornments {
    insertElements?: Array<{
        slot: AdornmentSlot;
        element: ReactNode;
    }>;
}
type AdornmentGenerator = (personId: string, jobId?: string) => ProfileAdornments;

interface ProfileAdornmentsContextType {
    pushAdornmentGenerator: (generator: AdornmentGenerator) => () => void;
    getAdornmentGenerator: () => AdornmentGenerator | null;
    getAdornmentsForPerson: (personId: string, jobId?: string) => ProfileAdornments;
}

const ProfileAdornmentsContext = createContext<ProfileAdornmentsContextType | undefined>(undefined);

interface ProfileAdornmentsProviderProps {
    children?: ReactNode;
}

const ProfileAdornmentsProvider = ({ children }: ProfileAdornmentsProviderProps): JSX.Element => {
    const [generatorStack, setGeneratorStack] = React.useState<Array<{ id: number; generator: AdornmentGenerator }>>([]);
    const nextIdRef = React.useRef(0);

    const pushAdornmentGenerator = React.useCallback((generator: AdornmentGenerator) => {
        const id = nextIdRef.current;
        nextIdRef.current += 1;
        setGeneratorStack((stack) => [...stack, { id, generator }]);
        return () => {
            setGeneratorStack((stack) => stack.filter((entry) => entry.id !== id));
        };
    }, []);

    const getAdornmentGenerator = React.useCallback(() => {
        const currentEntry = generatorStack[generatorStack.length - 1];
        return currentEntry?.generator ?? null;
    }, [generatorStack]);

    const getAdornmentsForPerson = React.useCallback(
        (personId: string, jobId?: string): ProfileAdornments => {
            const currentGenerator = generatorStack[generatorStack.length - 1]?.generator;
            return currentGenerator?.(personId, jobId) ?? {};
        },
        [generatorStack]
    );

    const contextValue = React.useMemo(
        () => ({ pushAdornmentGenerator, getAdornmentsForPerson, getAdornmentGenerator }),
        [pushAdornmentGenerator, getAdornmentsForPerson, getAdornmentGenerator]
    );

    return <ProfileAdornmentsContext.Provider value={contextValue}>{children}</ProfileAdornmentsContext.Provider>;
};

function useProfileAdornments() {
    const context = useContext(ProfileAdornmentsContext);

    if (!context) {
        throw new Error('useProfileAdornments must be used within a ProfileAdornmentsProvider');
    }

    return context;
}

export { useProfileAdornments, ProfileAdornmentsProvider, AdornmentGenerator, AdornmentSlot };
